import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { getBlogPosts } from '../services/dataService';
import { BlogPost } from '../types';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';

const readingTime = (text: string) => {
  const words = (text ?? '').replace(/<[^>]+>/g, '').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

const formatDate = (iso: string) => {
  try { return format(parseISO(iso), 'd MMMM yyyy', { locale: nl }); }
  catch { return iso; }
};

const AuthorAvatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'sm' }) => {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const dim = size === 'md' ? 'w-9 h-9 text-sm' : 'w-7 h-7 text-xs';
  return (
    <div className={`${dim} rounded-full bg-brand-800 text-white font-bold flex items-center justify-center flex-shrink-0 ring-2 ring-white`}>
      {initials}
    </div>
  );
};

/* ── Skeleton card ─────────────────────────────────────────────── */
const SkeletonCard: React.FC = () => (
  <div className="flex flex-col bg-white rounded-3xl overflow-hidden border border-brand-100">
    <div className="h-52 skeleton" />
    <div className="p-6 flex flex-col gap-3">
      <div className="h-5 skeleton rounded-full w-3/4" />
      <div className="h-4 skeleton rounded-full w-full" />
      <div className="h-4 skeleton rounded-full w-2/3" />
      <div className="pt-4 mt-auto flex items-center gap-3">
        <div className="w-7 h-7 rounded-full skeleton" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-3 skeleton rounded-full w-24" />
          <div className="h-2.5 skeleton rounded-full w-16" />
        </div>
      </div>
    </div>
  </div>
);

/* ── Post card ─────────────────────────────────────────────────── */
const PostCard: React.FC<{ post: BlogPost; index: number }> = ({ post, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const mins = readingTime(post.content ?? post.excerpt ?? '');

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 70}ms` }}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
    >
      <Link
        to={`/blog/${post.slug || post.id}`}
        className="group flex flex-col bg-white rounded-3xl overflow-hidden border border-brand-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ease-out h-full cursor-pointer"
      >
        {/* Image */}
        <div className="relative h-52 overflow-hidden flex-shrink-0">
          {post.cover_image ? (
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700 ease-out"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-800 via-brand-700 to-brand-500 flex items-center justify-center">
              <BookOpen size={36} className="text-white/20" />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-serif text-[1.05rem] font-bold text-brand-900 mb-2.5 group-hover:text-brand-700 transition-colors duration-200 leading-snug line-clamp-2">
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="text-stone-500 text-sm leading-relaxed line-clamp-2 mb-4 flex-1">
              {post.excerpt}
            </p>
          )}
          <div className="flex items-center justify-between pt-4 border-t border-brand-50 mt-auto">
            <div className="flex items-center gap-2.5">
              <AuthorAvatar name={post.author} />
              <div>
                <p className="text-xs font-semibold text-brand-800 leading-none">{post.author}</p>
                <p className="text-[11px] text-stone-400 mt-0.5">{formatDate(post.created_at)}</p>
              </div>
            </div>
            <span className="w-8 h-8 rounded-full bg-brand-50 group-hover:bg-brand-900 flex items-center justify-center transition-all duration-300 flex-shrink-0 group-hover:scale-110">
              <ArrowRight size={13} className="text-brand-500 group-hover:text-white transition-colors" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

/* ── Main page ─────────────────────────────────────────────────── */
export const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getBlogPosts(page)
      .then((res) => {
        setPosts(res.posts ?? []);
        setTotalPages(res.pages ?? 1);
        setTotal(res.total ?? 0);
      })
      .catch(() => setError('Kon de blogberichten niet laden. Probeer het later opnieuw.'))
      .finally(() => setLoading(false));
  }, [page]);

  const changePage = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const featured = page === 1 ? posts[0] : null;
  const rest = page === 1 ? posts.slice(1) : posts;

  return (
    <div className="min-h-screen bg-brand-50">

      {/* ── Hero — matches Contact/Rent pattern ───────────────────── */}
      <div className="relative overflow-hidden bg-brand-900">
        {/* Warm gradient orb */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-700/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-[300px] h-[300px] rounded-full bg-brand-600/20 blur-3xl pointer-events-none" />
        {/* Subtle dot texture */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: 'radial-gradient(circle, #d4c5b2 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />

        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-600/40 bg-brand-800/60 text-brand-300 text-xs font-semibold uppercase tracking-widest mb-8">
              <BookOpen size={12} /> Blog
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6 tracking-tight">
              Verhalen &amp;<br />
              <span className="text-brand-300">Inspiratie</span>
            </h1>
            <p className="text-brand-300/80 text-lg md:text-xl leading-relaxed max-w-lg font-light">
              Ontdek de mooiste plekken, reisverhalen en tips van Chalet&nbsp;Inn.
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-600/40 to-transparent" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-16">

        {/* ── Loading skeletons ─────────────────────────────────── */}
        {loading && (
          <div className="space-y-14">
            <div className="rounded-[2rem] overflow-hidden h-[380px] skeleton" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map(i => <SkeletonCard key={i} />)}
            </div>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────── */}
        {error && !loading && (
          <div className="text-center py-32">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* ── Empty ─────────────────────────────────────────────── */}
        {!loading && !error && posts.length === 0 && (
          <div className="text-center py-32">
            <div className="w-16 h-16 rounded-full bg-brand-100 flex items-center justify-center mx-auto mb-5">
              <BookOpen size={28} className="text-brand-400" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-brand-900 mb-2">Nog geen berichten</h2>
            <p className="text-stone-400">Kom binnenkort terug voor nieuws en inspiratie.</p>
          </div>
        )}

        {!loading && !error && posts.length > 0 && (
          <>
            {/* ── Featured post — light card design ─────────────── */}
            {featured && (
              <Link
                to={`/blog/${featured.slug || featured.id}`}
                className="group block mb-16 cursor-pointer"
              >
                <div className="relative rounded-[2rem] overflow-hidden bg-white border border-brand-100 shadow-lg hover:shadow-2xl transition-all duration-300 ring-0">
                  <div className="md:grid md:grid-cols-5 min-h-[420px]">

                    {/* Image — 3/5 cols */}
                    <div className="md:col-span-3 relative overflow-hidden h-64 md:h-auto">
                      {featured.cover_image ? (
                        <img
                          src={featured.cover_image}
                          alt={featured.title}
                          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-brand-800 via-brand-700 to-brand-500 flex items-center justify-center">
                          <BookOpen size={56} className="text-white/15" />
                        </div>
                      )}
                      {/* Subtle right fade into white panel */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 md:to-white/20 hidden md:block" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-900/60 to-transparent md:hidden" />
                    </div>

                    {/* Content — 2/5 cols, light background */}
                    <div className="md:col-span-2 flex flex-col justify-center bg-white p-8 md:p-10 lg:p-12">
                      <div className="inline-flex items-center gap-2 mb-6">
                        <span className="w-6 h-px bg-brand-400" />
                        <span className="text-brand-500 text-[11px] font-bold uppercase tracking-[0.15em]">Uitgelicht</span>
                      </div>

                      <h2 className="font-serif text-2xl md:text-3xl font-bold text-brand-900 leading-tight mb-4 group-hover:text-brand-700 transition-colors duration-200 tracking-tight">
                        {featured.title}
                      </h2>

                      {featured.excerpt && (
                        <p className="text-stone-500 text-sm leading-relaxed line-clamp-3 mb-7">
                          {featured.excerpt}
                        </p>
                      )}

                      <div className="flex items-center gap-3 mb-7 pb-7 border-b border-brand-100">
                        <AuthorAvatar name={featured.author} size="md" />
                        <div>
                          <p className="text-brand-900 text-sm font-semibold leading-none">{featured.author}</p>
                          <p className="text-stone-400 text-xs mt-1">{formatDate(featured.created_at)}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-1.5 text-stone-400 text-xs">
                          <Clock size={11} /> {readingTime(featured.content ?? featured.excerpt ?? '')} min
                        </div>
                      </div>

                      <div className="inline-flex items-center gap-3 text-brand-900 text-sm font-semibold group-hover:gap-4 transition-all duration-300">
                        Lees het verhaal
                        <span className="w-9 h-9 rounded-full bg-brand-50 group-hover:bg-brand-900 flex items-center justify-center transition-all duration-300 flex-shrink-0 group-hover:scale-110">
                          <ArrowRight size={15} className="text-brand-600 group-hover:text-white transition-colors" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* ── Section heading ───────────────────────────────── */}
            {rest.length > 0 && (
              <div className="flex items-center gap-4 mb-10">
                <h2 className="font-serif text-xl font-bold text-brand-900 whitespace-nowrap">
                  {featured ? 'Meer verhalen' : 'Alle verhalen'}
                </h2>
                <div className="flex-1 h-px bg-brand-200/60" />
                <span className="text-xs text-stone-400 font-medium whitespace-nowrap">
                  {total} {total === 1 ? 'bericht' : 'berichten'}
                </span>
              </div>
            )}

            {/* ── Post grid ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post, i) => (
                <PostCard key={post.id} post={post} index={i} />
              ))}
            </div>

            {/* ── Pagination ────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-16">
                <button
                  onClick={() => changePage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="w-10 h-10 rounded-full border border-brand-200 bg-white hover:bg-brand-50 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 shadow-sm cursor-pointer"
                  aria-label="Vorige pagina"
                >
                  <ChevronLeft size={15} className="text-brand-700" />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => changePage(p)}
                    className={`w-10 h-10 rounded-full text-sm font-bold transition-all duration-200 cursor-pointer ${
                      p === page
                        ? 'bg-brand-900 text-white scale-110 shadow-md'
                        : 'bg-white border border-brand-200 text-brand-700 hover:bg-brand-50 shadow-sm'
                    }`}
                    aria-label={`Pagina ${p}`}
                    aria-current={p === page ? 'page' : undefined}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => changePage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="w-10 h-10 rounded-full border border-brand-200 bg-white hover:bg-brand-50 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 shadow-sm cursor-pointer"
                  aria-label="Volgende pagina"
                >
                  <ChevronRight size={15} className="text-brand-700" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
